import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { IFeeData } from '../merchants/FeeTableRow';
import { toaster } from 'rsuite';
import { useTranslation } from 'react-i18next';
import { IModalWindow, MODAL_TYPE, ModalWindow } from '../modal/ModalWindow';
import {
	IApplicationDetails,
	getFormattedFormFields,
	FORM_FIELDS,
	getInitialValues,
	getFormattedFormikFeeData,
	convertToApplicationInitialData,
	convertToInitialFeeValues,
	getAdminApplicationDetailsSchema,
	getPartnerAdminApplicationDetailsSchema,
} from '../../constants/new-application/new-application-form';
import { parseJwt, STATUSES, STATUSES_CODES } from '../../views/login/Login';
import { checkIfValidUUID, PageLink, PageTitle } from '../../layout/core';
import BusinessInformationCard from './BusinessInformationCard';
import { errorHandler } from '../../utils/newApplicationUtils';
import toastMessage from '../ToastMessage';
import NewApplicationHeader from './NewApplicationHeader';
import ContactInformationCard from './ContactInformationCard';
import BankInformationCard from './BankInformationCard';
import SpecialTermsCard from './SpecialTermsCard';
import NewApplicationFooter from './NewApplicationFooter';
import AddressCard from './AddressCard';
import { CustomerSupportInformationCart } from './CustomerSupportInformationCart';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { ImageCropper } from '../common/ImageCropper';
import { Area } from 'react-easy-crop';
import { getCroppedImg } from '../../utils/imageCrop';
import { DeveloperInformationCart } from './DeveloperInformationCart';
import { editNewApplicant, saveNewApplicant } from '../../api/new-application-api-service';
import { setApplicationEditStatus } from '../../store/merchnts/actions/application';
import { fetchFees } from '../../store/merchnts/actions/fees';
import { fetchMccList, fetchMerchantsLists } from '../../store/merchnts/actions/merchant';
import { fetchAgencyList } from '../../store/merchnts/actions/agents';
import { sendSentryMessage } from '../../utils/sentry-helper';
import { ApplicationOwner } from './ApplicationOwner';
import { IOwners } from '../../interfaces/merchant';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { fetchIntegrationPartnersList, fetchWhiteListedPartnersList } from '../../store/merchnts/actions/partners';
import { Spinner } from '../Spinner';

import './NewApplication.scss';
import { NewApplicationAccountInformation } from './NewApplicationAccountInformation';
import { BusinessModel } from './BusinessModel';
import { ProductInformation } from './ProductInformation';
import { VolumeEstimates } from './VolumeEstimates';
import { fetchAccountManagerList } from '../../store/identyti/actions/application';
import { useIdentity } from '../../store/identyti/identity-context';
import { QuickApplicationFees } from './quick-application/QuickApplicationFees';
import { APPLICATION_FEE_INPUTS } from '../../constants/new-application/application-fee-inputs';
import {
	getFormattedInitialFeeValues,
	getFormattedInitialFeeValuesWithId,
} from '../../constants/new-application/quick-application/fee-form';
import { merchantActionTypes } from '../../store/merchnts/action-types';
import { fetchAccountList } from '../../store/merchnts/actions/account';
import { APPLICATION_DETAILS_NAVIGATION } from '../../constants/application-details/application-details-navigation';
import { CreateNewApplicationSuccessContent } from './quick-application/QuickApplicationFooter';
import {
	QUICK_APPLICATION_FORM_FIELDS
} from '../../constants/new-application/quick-application/quick-application-form';

export enum FEE_TYPE {
	STANDARD = 'standard',
	NON_STANDARD = 'non-standard'
}

export interface ISelectorData {
	value: string;
	label: string;
	role?: string;
	companyName?: string;
	description?: string;
}

export interface IModalState {
	isVisible: boolean;
	modalType?: MODAL_TYPE;
	modalText?: string;
	modalTitle?: string;
	cancelButtonText?: string;
	confirmButtonText?: string;
	onClose?: () => void;
	onConfirm?: () => void;
	onCancel?: () => void;
	modalContent?: JSX.Element;
	minHeight?: number;
}

export const breadcrumbs = (t): Array<PageLink> => ([
	{
		title: t('NEW_APPLICATION.APPLICATIONS'),
		path: '/accounts/applications',
		isSeparator: false,
		isActive: false
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false
	}
]);

export const NewApplication = (): JSX.Element => {
	const { applicationId: id } = useParams<{ applicationId: string }>();
	const applicationId = checkIfValidUUID(id) ? id : undefined;
	const navigate = useNavigate();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const parsedTokenData = parseJwt(authUser.idToken);
	let partnerId = '';
	let integrationPartnerId = '';
	// TODO check partners permissions
	if (checkRoles(authUser, CLAIMS.PARTNER_ADMIN)) {
		partnerId = parsedTokenData?.['custom:pid'];
	} else if (checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN)) {
		integrationPartnerId = parsedTokenData?.['custom:pid'];
	}
	const { t } = useTranslation();
	const { dispatch: IdentityDispatch } = useIdentity();
	const { merchantState, dispatch } = useMerchants();
	const {
		standardFees,
		merchantFee,
		applicationData: data,
		applicationFormEditStatus,
		isAccountListLoading,
		isWhitelabelPartnersListSelectorLoading,
		isIntegrationPartnersListSelectorLoading,
		isApplicationDataLoading,
		merchantsLists,
	} = merchantState;

	console.log('applicationData---', data);

	const [ownerData, setOwnerData] = useState<IOwners[]>([]);
	const [applicationStatus, setApplicationStatus] = useState<STATUSES>(STATUSES.DRAFT);
	const [isClicked, setIsClicked] = useState(false);
	const [selectedFile, setSelectedFile] = useState();
	const [preview, setPreview] = useState();

	const [uploadedFile, setUploadedFile] = useState<string | undefined>();
	const [isDisable, setIsDisable] = useState<boolean>(false);
	const [pixelCrop, setPixelCrop] = useState<Area | undefined>();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isCreateNew, setCreateNew] = useState<boolean>(false);
	// const [partnerList, setPartnerList] = useState<ISelectorData[]>(partnersListSelector);
	const [activeFee, setActiveFee] = useState<IFeeData>(standardFees);
	const [nonStandardFee, setNonStandardFee] = useState<IFeeData>(merchantFee);
	const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [checkBoxes, setCheckBoxes] = useState<string[]>([]);

	const getValidationSchema = (): any => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			return getAdminApplicationDetailsSchema(t);
		}
		if (checkRoles(authUser, CLAIMS.PARTNER_ADMIN)) {
			return getPartnerAdminApplicationDetailsSchema(t);
		}
	};

	const formik = useFormik<IApplicationDetails>({
		initialValues: getInitialValues({ partnerId, integrationPartnerId }),
		validationSchema: getValidationSchema(),
		onSubmit: () => void 0
	});

	useEffect(() => {
		if (!merchantsLists?.countries?.length) {
			void fetchMerchantsLists(dispatch);
		}
		if (!applicationId) {
			dispatch({ type: merchantActionTypes.SET_APPLICATION_DATA_LOADING, payload: false });
		}
	}, []);

	useEffect(() => {
		if (!applicationId) {
			setApplicationEditStatus({ dispatch, value: true });
		}
	}, []);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			void fetchWhiteListedPartnersList(dispatch);
		}
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
			void fetchIntegrationPartnersList(dispatch);
		}
	}, []);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			void fetchAgencyList(dispatch);
		}
	}, []);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
			void fetchAccountList(dispatch);
		}
	}, []);

	useEffect(() => {
		void fetchAccountManagerList(IdentityDispatch);
	}, []);

	useEffect(() => {
		if (!selectedFile) {
			setPreview(undefined);
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFile);
		// @ts-ignore
		setPreview(objectUrl);

		return (): void => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	const onSelectFile = (e): void => {
		if (!e.target?.files || e.target?.files?.length === 0) {
			setSelectedFile(undefined);
			return;
		}
		const files = e.target.files;
		const reader = new FileReader();
		reader.readAsDataURL(files[0]);

		reader.onload = function (): any {
			formik.setFieldValue('LogoFile', reader.result);
			formik.setTouched({ 'LogoFile': true });
		};
		reader.onerror = function (): any {
			formik.setTouched({ 'LogoFile': true });
		};
		setIsModalOpen(true);
		setSelectedFile(files[0]);
	};

	const onEditFile = (): void => {
		if (preview) {
			setIsModalOpen(true);
		}
	};

	const onDeleteFile = (): void => {
		formik.setFieldValue(FORM_FIELDS.LOGO, '');
		setUploadedFile(undefined);
		setPreview(undefined);
	};

	const onModalSave = async (): Promise<void> => {
		try {
			const { file, fileURL } = await getCroppedImg({
				imageSrc: preview,
				pixelCrop
			});
			formik.setFieldValue(FORM_FIELDS.LOGO, file);
			setUploadedFile(fileURL);
			setIsDisable(true);
		} catch (e) {
			console.error(e);
		}
		setIsModalOpen(false);
	};

	const modalClose = (): void => setIsModalOpen(false);

	const handleNotificationModalClose = (): void => navigate(`/accounts/applications`);

	const closeSaveAndSendInformationModal = (): void => setSaveModalOpen(false);


	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
			void fetchFees(dispatch, activeFee, nonStandardFee);
		}
	}, []);

	useEffect(() => {
		void fetchMccList(dispatch);
	}, []);

	useEffect(() => {
		if (data) {
			const formikInitialValue = convertToApplicationInitialData(data);
			let convertedFees = {};
			if (data?.logoUrl) {
				setUploadedFile(data?.logoUrl);
			}
			if (data?.fees) {
				const newFees: any = {};

				data?.fees.forEach(el => {
					switch (el.name) {
						case 'Percentage':
							newFees.percentage = el.amount / 100;
							newFees.percentageId = el.id;
							newFees.minFeeAmount = el?.minimumFeeAmount >=0 ? el?.minimumFeeAmount / 100 : '';
							break;
						case 'Fee Amount':
							newFees.feeAmount = el.amount / 100;
							newFees.feeAmountId = el.id;
							break;
						case 'Chargeback Fee':
							newFees.chargeback = el.amount / 100;
							newFees.chargebackId = el.id;
							break;
						case 'Refund Fee':
							newFees.refundFee = el.amount / 100;
							newFees.refundFeeId = el.id;
							break;
						case 'Risk Fee':
							newFees.riskFee = el.amount / 100;
							newFees.riskFeeId = el.id;
							break;
					}
				});
				convertedFees = convertToInitialFeeValues(newFees);
			}
			const convertedApplicationData = {
				...formikInitialValue,
				...convertedFees
			};
			formik.setValues(convertedApplicationData);
		}
	}, [data]);

	useEffect(() => {
		if (data?.owners) {
			setOwnerData(data?.owners);
		}

		if (data?.fees) {
			const newNSF = {
				...nonStandardFee
			};

			!data?.standardFeeSelected && data?.fees?.forEach(el => {
				switch (el?.name) {
					case 'Percentage':
						newNSF.percentage = el.amount / 100;
						newNSF.percentageId = el.id;
						newNSF.minFeeAmount = el?.minimumFeeAmount ? el?.minimumFeeAmount / 100 : '';
						break;
					case 'Fee Amount':
						newNSF.feeAmount = el.amount / 100;
						newNSF.feeAmountId = el.id;
						break;
					case 'Chargeback Fee':
						newNSF.chargeback = el.amount / 100;
						newNSF.chargebackId = el.id;
						break;
					case 'Refund Fee':
						newNSF.refundFee = el.amount / 100;
						newNSF.refundFeeId = el.id;
						break;
					case 'Risk Fee':
						newNSF.riskFee = el.amount / 100;
						newNSF.riskFeeId = el.id;
						break;
				}
			});
			setApplicationStatus(data?.statusName);
			setNonStandardFee(newNSF);
		} else {
			setNonStandardFee(merchantFee);
			setApplicationStatus(STATUSES.DRAFT);
		}
	}, [data]);


	const handleCreateSuccessModal = (applicationId: string, isSend?:boolean): void => {
		closeSaveAndSendInformationModal();
		setModalState({
			isOpen: true,
			modalTitle: 'Application created',
			modalType: MODAL_TYPE.APPROVE,
			content: <CreateNewApplicationSuccessContent isSend={isSend} email={formik.values[FORM_FIELDS.EMAIL]} />,
			confirmButtonText: 'To application',
			onConfirm: (): void => navigate(`/accounts/applications/${applicationId}/${APPLICATION_DETAILS_NAVIGATION.FORM}`),
			cancelButtonText: 'Back to list',
			onCancel: (): void => navigate(`/accounts/applications`),
		});
	};

	const handleSaveAndSendSuccessModal = (): void => {
		closeSaveAndSendInformationModal();
		setModalState({
			isOpen: true,
			modalTitle: 'Application saved',
			modalType: MODAL_TYPE.APPROVE,
			minHeight: 150,
			content: (
				<div className="fs-5 mb-10 px-8">
					<div className="mb-5 fw-bolder text-center">
						{t('QUICK_APPLICATION.SUCCESS_MODAL_TITLE_WITH_INVITATION_CREATED_APP')}
					</div>
					<div className="mb-10">
						<span>{t('QUICK_APPLICATION.SUCCESS_MODAL_TEXT_WITH_INVITATION_P1_CREATED_APP')} </span>
						<span className="fw-bolder">{formik.values[FORM_FIELDS.EMAIL]} </span>
						<span>{t('QUICK_APPLICATION.SUCCESS_MODAL_TEXT_WITH_INVITATION_P2')}</span>
					</div>

					<div>
						<span className="fw-bolder">Note: </span>
						<span>{t('QUICK_APPLICATION.SUCCESS_MODAL_NOTE_WITH_INVITATION')}</span>
					</div>

				</div>
			),
			confirmButtonText: 'Back to list',
			onConfirm: (): void => navigate('/accounts/applications'),
		});
	};

	const formattedFieldsFunction = (fields): any => {
		const formattedFields = getFormattedFormFields(formik.values);
		//TODO Refactoring required
		fields?.forEach(field => {
			if (!Object.values(formattedFields[field] || {})?.some(el => el)) {
				formattedFields[field] = null;
			}
		});
		return formattedFields;
	};

	const onSave = async (onDoneClick?: boolean): Promise<void> => {
		if (applicationId) {
			setIsClicked(true);
			try {
				const fields = formattedFieldsFunction(['developerContact', 'customerSupportInformation']);
				const formattedFees = getFormattedFormikFeeData(formik.values);
				let newData = {
					// ...getFormattedFormFields(formik.values),
					...fields,
					status: applicationStatus,
					id: applicationId,
					owners: ownerData,
				};

				if (data) {
					newData = {
						...data,
						...newData,
						standardFeeSelected: false,
						fees: getFormattedInitialFeeValuesWithId(formattedFees)
						// standardFeeSelected: activeFee.feeType === FEE_TYPE.STANDARD,
						// fees: nonStandardFee.percentageId
						// 	? addedFeesValue({ activeFee, nonStandardFee, applicationId })
						// 	: feesValue({ activeFee, nonStandardFee }),

					};
				}
				// newData.productInfo.trafficAcquisition = checkBoxes;

				if (newData.logoUrl === uploadedFile) {
					newData.logoFile = null;
				}

				if (newData.logoUrl || newData.logoUrl === null) {
					delete newData.logoUrl;
				}

				const res = await editNewApplicant(newData, applicationId, formik);

				toaster.push(
					toastMessage({
						text: (res as any)?.success ? 'Application was saved!' : (res as any)?.errors?.[0]?.item1,
						type: (res as any)?.success ? 'success': 'error'
					}),
					{ placement: 'topEnd' }
				);
				if (onDoneClick) {
					setApplicationEditStatus({ dispatch, value: false });
				}
			} catch (e) {
				console.log('change app error', e.response);
				// errorHandler({ e, formik });
			}
			setIsClicked(false);
		} else {
			setIsClicked(true);
			try {
				const fields = formattedFieldsFunction(['developerContact', 'customerSupportInformation']);
				const formattedFees = getFormattedFormikFeeData(formik.values);
				const params = {
					...fields,
					status: applicationStatus,
					owners: ownerData,
					standardFeeSelected: false,
					fees: getFormattedInitialFeeValues(formattedFees),
					// standardFeeSelected: activeFee.feeType === FEE_TYPE.STANDARD,
					// fees: feesValue({ activeFee, nonStandardFee }),
					ResendForReview: true
				};
				// params.productInfo.trafficAcquisition = checkBoxes;

				const res = await saveNewApplicant(params, formik);

				handleCreateSuccessModal((res as any)?.applicationId);
			} catch (e) {
				console.log('onSave error', e.response);
				setIsClicked(false);
				// errorHandler({ e, formik });
			}
		}
	};

	const onSaveAndSend = async (send?: boolean): Promise<void> => {
		console.log('onDoneClick2', send);
		const fields = formattedFieldsFunction(['developerContact', 'customerSupportInformation']);
		const formattedFees = getFormattedFormikFeeData(formik.values);
		if (applicationId) {
			try {
				let newData;

				if (data) {
					newData = {
						...data,
						...fields,
						owners: ownerData,
						// ...getFormattedFormFields(formik.values),
						status: STATUSES_CODES.IN_REVIEW,
						statusName: STATUSES.IN_REVIEW,
						resendForReview: true,
						send,
						id: applicationId,
						Selected: false,
						fees: getFormattedInitialFeeValuesWithId(formattedFees)
						// standardFeeSelected: activeFee.feeType === FEE_TYPE.STANDARD,
						// fees: nonStandardFee.percentageId
						// 	? addedFeesValue({ activeFee, nonStandardFee, applicationId })
						// 	: feesValue({ activeFee, nonStandardFee })
					};
				}
				// newData.productInfo.trafficAcquisition = checkBoxes;

				if (newData.logoUrl === uploadedFile) {
					newData.logoFile = null;
				}

				if (newData.logoUrl || newData.logoUrl === null) {
					delete newData.logoUrl;
				}

				const res = await editNewApplicant(newData, applicationId, formik);
				if (!(res as any).success) {
					closeSaveAndSendInformationModal();
					toaster.push(toastMessage({
						text: (res as any)?.errors?.[0]?.item1,
						type: 'error'
					}), { placement: 'topEnd' });
				} else {
					handleSaveAndSendSuccessModal();
				}
			} catch (e) {
				sendSentryMessage({ name: `PUT OnBoarding error`, context: e });
				console.log('change app error', e);
				closeSaveAndSendInformationModal();
				// errorHandler({ e, formik });
			}
		} else {
			try {
				const newData = {
					// ...getFormattedFormFields(formik.values),
					...fields,
					owners: ownerData,
					status: applicationStatus,
					send: Boolean(send),
					standardFeeSelected: false,
					fees: getFormattedInitialFeeValues(formattedFees),
					// standardFeeSelected: activeFee.feeType === FEE_TYPE.STANDARD,
					// fees: feesValue({ activeFee, nonStandardFee }),
					productInfo: {
						...fields.productInfo,
						// trafficAcquisition: checkBoxes
					}
				};

				const res = await saveNewApplicant(newData, formik);

				handleCreateSuccessModal((res as any)?.applicationId, true);
			} catch (e) {
				sendSentryMessage({ name: `POST OnBoarding error`, context: e });
				closeSaveAndSendInformationModal();
				// errorHandler({ e, formik });
			}
		}
		setIsClicked(false);
	};

	// const updateEditStatus = (): void => {
	// 	const err = Object.keys(formik.errors || {});
	// 	if (err.length) {
	// 		const input = document.querySelector(
	// 			`input[name=${err[0]}]`
	// 		);
	//
	// 		input?.scrollIntoView({
	// 			behavior: 'smooth',
	// 			block: 'center',
	// 			inline: 'start'
	// 		});
	// 	}
	// 	if (formEditStatus) {
	// 		formik.handleSubmit();
	// 		if (formik.isValid) {
	// 			onSave(true);
	// 		}
	// 	} else {
	// 		setFormEditStatus(prevState => !prevState);
	// 	}
	// };

	if (isApplicationDataLoading || isAccountListLoading || isWhitelabelPartnersListSelectorLoading || isIntegrationPartnersListSelectorLoading) {
		return (
			<div className="card h-100">
				<div className="card-body">
					<Spinner pageLoader/>
				</div>
			</div>
		);
	}
	const FEE_INPUTS = APPLICATION_FEE_INPUTS(t);

	return (
		<>
			<PageTitle breadcrumbs={breadcrumbs(t)}>
				{applicationId ? applicationId : t('NEW_APPLICATION.NEW_APPLICATION')}
			</PageTitle>
			<div>
				{
					!applicationId && <NewApplicationHeader />
				}

				<form className="form">
					<BusinessInformationCard
						setCreateNew={setCreateNew}
						formik={formik}
						onSelectFile={onSelectFile}
						formEditStatus={applicationFormEditStatus}
						isDisable={isDisable}
						uploadedFile={uploadedFile}
						onEditFile={onEditFile}
						onDeleteFile={onDeleteFile}
					/>

					<VolumeEstimates formik={formik} formEditStatus={applicationFormEditStatus} isRequired={false} />

					<NewApplicationAccountInformation formik={formik} formEditStatus={applicationFormEditStatus}/>

					<BusinessModel
						formik={formik}
						formEditStatus={applicationFormEditStatus}
						isRequired={false}
						urls={data?.businessModel?.urls ?? []}
					/>

					{/*<ProductInformation*/}
					{/*	formik={formik}*/}
					{/*	formEditStatus={applicationFormEditStatus}*/}
					{/*	data={data?.productInfo?.trafficAcquisition}*/}
					{/*	setCheckBoxes={setCheckBoxes}*/}
					{/*	isRequired={false}*/}
					{/*/>*/}

					<ContactInformationCard formik={formik} formEditStatus={applicationFormEditStatus} isRequired={false} />

					<ApplicationOwner
						appOwnerData={ownerData}
						setAppOwnerData={setOwnerData}
						formEditStatus={applicationFormEditStatus}
						showActions={true}
						showAddButton={true}
					/>

					{/*<DeveloperInformationCart formik={formik} formEditStatus={applicationFormEditStatus}/>*/}

					{/*<CustomerSupportInformationCart formik={formik} formEditStatus={applicationFormEditStatus} isRequired={false} />*/}

					<AddressCard formik={formik} formEditStatus={applicationFormEditStatus}/>

					<BankInformationCard
						formEditStatus={applicationFormEditStatus}
						formik={formik}
					/>

					{
						!formik.values?.[QUICK_APPLICATION_FORM_FIELDS.CONTRACT_ALREADY_SIGNED] && (
							<SpecialTermsCard
								formik={formik}
								formEditStatus={applicationFormEditStatus}
								fieldName={FORM_FIELDS.SPECIAL_TERMS}
								// label={t(FORM_FIELDS.SPECIAL_TERMS)}
							/>
						)
					}

					{/*{checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (*/}
					{/*	<FeesCard*/}
					{/*		formEditStatus={formEditStatus}*/}
					{/*		activeFee={activeFee}*/}
					{/*		applicationId={applicationId}*/}
					{/*		setActiveFee={setActiveFee}*/}
					{/*		nonStandardFee={nonStandardFee}*/}
					{/*		setNonStandardFee={setNonStandardFee}*/}
					{/*	/>*/}
					{/*)}*/}
						<QuickApplicationFees
							formEditStatus={applicationFormEditStatus}
							formik={formik}
							inputs={FEE_INPUTS}
						/>
				</form>


				{
					checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) &&
					applicationStatus !== STATUSES.CANCELLED &&
					applicationStatus !== STATUSES.DECLINED &&
					(
						<NewApplicationFooter
							saveAndSendModalOpen={(): void => setSaveModalOpen(true)}
							onSave={onSave}
							isClicked={isClicked}
							formEditStatus={applicationFormEditStatus}
							formik={formik}
							applicationStatus={applicationStatus}
						/>
					)
				}
			</div>

			<ModalWindow
				{...modalState}
				onClose={handleNotificationModalClose}
			/>

			<ModalWindow
				isOpen={saveModalOpen}
				modalType={MODAL_TYPE.SAVE}
				modalText={t('NEW_APPLICATION.SEND_APP_TEXT')}
				modalTitle={t('NEW_APPLICATION.SEND_APP_TITLE')}
				onClose={closeSaveAndSendInformationModal}
				onCancel={closeSaveAndSendInformationModal}
				onConfirm={(): Promise<void> => onSaveAndSend(true)}
			/>

			<ModalWindow
				isOpen={isModalOpen}
				modalTitle={t('COLOR_MODULE.CROP_TITLE')}
				confirmButtonText={t('COLOR_MODULE.SAVE')}
				onClose={modalClose}
				onCancel={modalClose}
				onConfirm={onModalSave}
				contentClassName="position-relative"
				content={
					<ImageCropper
						image={preview}
						setPixelCrop={setPixelCrop}
						aspect={34/ 10}
					/>
				}
			/>
		</>
	);
};
